import {
  createCollection,
  localOnlyCollectionOptions,
} from '@platform/db/tanstack-db';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment } from '@/types';

/**
 * Local-only collection for order entry table items, used for presentation/rendering.
 *
 * This collection stores transformed order entry data optimized for table display,
 * including pre-formatted currency strings, pack display strings, and gross profit
 * percentages. Items are keyed by `variantPackUUID` for fast lookups and updates.
 *
 * @see {@link OrderEntryTableRowData} for the full type definition
 * @see {@link transformOrderEntryItem} for the transformation logic
 *
 * @example
 * ```ts
 * // Example of data contained in this collection:
 * const orderEntryItem: OrderEntryTableRowData = {
 *   variantPackUUID: 'vp-abc-123',
 *   externalItemId: 'SKU-001',
 *   name: 'Organic Roma Tomatoes',
 *   description: 'Fresh organic roma tomatoes, 25lb case',
 *   packDisplayString: '25 LB',
 *   priceDisplayString: '$42.99',
 *   grossProfitPercentageDisplayString: '22.5%',
 *   priceMicros: 4299000,
 *   imageUrl: 'https://cdn.example.com/tomatoes.png',
 *   orderGuideCollectionIds: ['favorites', 'produce'],
 *   priceEditDisplays: {
 *     landed_cost: { display: '$38.50', raw: 38.5 },
 *     margin: { display: '10.4%', raw: '10.4%' },
 *   },
 *   labels: {
 *     availability_label: {
 *       i18n_key: 'limited_availability',
 *       level: BadgeVariant.WARNING,
 *     },
 *   },
 * };
 *
 * // Usage:
 * orderEntryCollection.insert(orderEntryItem);
 * const item = orderEntryCollection.get('vp-abc-123');
 * ```
 */
export const orderEntryCollection = createCollection(
  localOnlyCollectionOptions<OrderEntryTableRowData>({
    getKey: (item) => item.variantPackUUID,
    id: 'order-entry-items',
  }),
);

/**
 * Local-only collection for raw order guide items, used for calculations and mutations.
 *
 * This collection stores the original GraphQL fragment data as returned from the API,
 * preserving raw values (e.g., prices in micros, unformatted percentages) needed for
 * cart operations, price calculations, and order mutations. Items are keyed by
 * `variant_pack_id` for fast lookups.
 *
 * @see {@link SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment} for the full type
 * @see {@link orderEntryCollection} for the presentation-ready transformed data
 *
 * @example
 * ```ts
 * // Example of data contained in this collection (raw GraphQL fragment):
 * const orderGuideItem: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment = {
 *   __typename: 'OrderEntryItem',
 *   variant_pack_id: 'vp-abc-123',
 *   order_guide_collection_ids: ['favorites', 'produce'],
 *   item_info: {
 *     __typename: 'OrderEntryItemInfo',
 *     name: 'Organic Roma Tomatoes',
 *     description: 'Fresh organic roma tomatoes, 25lb case',
 *     image_url: 'https://cdn.example.com/tomatoes.png',
 *   },
 *   order_info: {
 *     __typename: 'OrderEntryOrderInfo',
 *     cart_quantity: 3,
 *   },
 *   variant_pack: {
 *     __typename: 'variants_packs',
 *     uuid: 'vp-abc-123',
 *     external_item_id: 'SKU-001',
 *     pack: {
 *       __typename: 'packs',
 *       uuid: 'pack-xyz',
 *       unit: 'LB',
 *       min_unit_size: 25,
 *       max_unit_size: 25,
 *     },
 *   },
 *   variant_pack_info: {
 *     __typename: 'OrderEntryVariantPackInfo',
 *     currency: 'USD',
 *     pack_price_in_micros: 4299000,
 *     pack_cost_in_micros: 3350000,
 *     unit_price_in_micros: 171960,
 *     unit_cost_in_micros: 134000,
 *     gross_profit_in_percentage: 22.5,
 *   },
 *   price_edit_display_info: [
 *     { __typename: 'OrderEntryPriceEditDisplayInfo', key: 'landed_cost', value: '38.50', currency: 'USD', trend: null },
 *     { __typename: 'OrderEntryPriceEditDisplayInfo', key: 'margin', value: '10.4%', currency: null, trend: 'UP' },
 *   ],
 *   labels: {
 *     __typename: 'OrderEntryLabels',
 *     availability_label: {
 *       __typename: 'OrderEntryAvailabilityLabel',
 *       i18n_key: 'limited_availability',
 *       level: 'WARNING',
 *     },
 *   },
 * };
 *
 * // Usage:
 * orderGuideItemCollection.insert(orderGuideItem);
 * const item = orderGuideItemCollection.get('vp-abc-123');
 * const priceInMicros = item?.variant_pack_info?.pack_price_in_micros; // 4299000
 * ```
 */
export const orderGuideItemCollection = createCollection(
  localOnlyCollectionOptions<SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment>(
    {
      getKey: (item) => item.variant_pack_id,
      id: 'order-guide-items',
    },
  ),
);
